import { LinearProgress } from "@mui/material";
import {
  client,
  connectMissions,
  type Missions,
} from "@rescuetablet/missions-client";
import { MissionsContextProvider } from "@rescuetablet/missions-client/react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import SignIn from "./SignIn";
import { useLocalStorage } from "./localStorage";

export type AuthContext = {
  signOut: () => void;
};

const Context = createContext<AuthContext | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [apiKey, setApiKey] = useLocalStorage<string>("apiKey");

  return apiKey ? (
    <ErrorBoundary
      FallbackComponent={({ error, resetErrorBoundary }) => (
        <SignIn
          error={error}
          signIn={(apiKey) => {
            resetErrorBoundary();
            setApiKey(apiKey);
          }}
        />
      )}
    >
      <Authenticated apiKey={apiKey} signOut={() => setApiKey(undefined)}>
        {children}
      </Authenticated>
    </ErrorBoundary>
  ) : (
    <SignIn signIn={setApiKey} />
  );
}

function Authenticated({
  apiKey,
  signOut,
  children,
}: PropsWithChildren<{ apiKey: string; signOut: () => void }>) {
  const [missions, setMissions] = useState<Missions>();
  const [error, setError] = useState<Error>();

  useEffect(() => {
    connectMissions({ apiKey })
      .then((missions) => {
        setMissions(missions);
        client.setConfig(missions.client.getConfig());
      })
      .catch(setError);
  }, [apiKey]);

  if (missions) {
    return (
      <Context.Provider value={{ signOut }}>
        <MissionsContextProvider missions={missions}>
          {children}
        </MissionsContextProvider>
      </Context.Provider>
    );
  }
  if (error) throw error;
  return <LinearProgress />;
}

export function useAuth(): AuthContext {
  return useContext(Context)!!;
}
